import type { FulfillmentOrderLine } from '@/infrastructure/FulfillmentAdminClient';

export interface LinePickerProps {
  lines: FulfillmentOrderLine[];
  /** The line the form is about; null while nothing is picked. The dialog owns the choice and its default. */
  selectedLineNo: number | null;
  onSelect: (line: FulfillmentOrderLine) => void;
  /** One radio group per dialog, so two pickers on a page never share a selection. */
  radioName: string;
}

/**
 * The product a dialog is about, picked from the order's lines: one radio per line with its SKU, name and quantity,
 * the whole row clickable. COGS Report and Catalog Request share it (SSD-7384), so a change to how a line is picked
 * lands in both forms at once. Fixed columns, so the table keeps its shape whichever line is picked.
 */
export function LinePicker({ lines, selectedLineNo, onSelect, radioName }: LinePickerProps) {
  return (
    <table className="cr-lines" aria-label="Pick the product">
      <colgroup>
        <col className="cr-col-pick" />
        <col className="cr-col-line" />
        <col />
        <col className="cr-col-qty" />
      </colgroup>
      <thead>
        <tr>
          <th aria-label="Pick" />
          <th>Line</th>
          <th>Product</th>
          <th>Qty</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr
            key={line.lineNo}
            className={line.lineNo === selectedLineNo ? 'cr-picked' : undefined}
            onClick={() => onSelect(line)}
          >
            <td>
              <input
                type="radio"
                name={radioName}
                aria-label={`Line ${line.lineNo}`}
                checked={line.lineNo === selectedLineNo}
                onChange={() => onSelect(line)}
              />
            </td>
            <td>{line.lineNo}</td>
            <td>
              <div className="cr-sku">{line.sku}</div>
              <div className="cr-name">{line.productName || line.productNameKr || '—'}</div>
            </td>
            <td>{line.totalQuantity}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
